import { useId } from "react";
import type { ReactNode } from "react";
import { DialogShell } from "./DialogShell";
import "./EmployeeFormDialog.css";
import "./EquipmentDialog.css";

/**
 * ef-061-8 — the frame the four equipment dialogs share (add / edit, log hours, add reminder, mark done). Since the
 * one-dialog-shell refactor it is a thin wrapper over `DialogShell` (overlay, panel, focus trap, Escape / overlay close,
 * `role="dialog"` named by its heading, `data-state` = ready | saving); what it adds is the single `alertId` the field
 * errors point at. The CALLER returns focus to its trigger and supplies `footer` (Cancel + the submit, which names its form).
 * Field vocabulary is EmployeeFormDialog.css's `emp-dialog*`, shared like PieceTypeDialog.
 */
export function EquipmentDialogShell({
  title,
  saving,
  onClose,
  footer,
  children,
}: {
  title: string;
  saving: boolean;
  onClose: () => void;
  footer: ReactNode;
  children: (ids: { alertId: string }) => ReactNode;
}) {
  const alertId = useId();
  return (
    <DialogShell title={title} onClose={onClose} busy={saving} footer={footer}>
      {children({ alertId })}
    </DialogShell>
  );
}

/** ONE `role="alert"` per dialog, so a screen reader hears a single announcement. */
export function DialogAlert({ id, message }: { id: string; message: string | null }) {
  if (!message) return null;
  return (
    <div id={id} role="alert">
      <p className="emp-dialog__error">{message}</p>
    </div>
  );
}

/** Which of `candidates` a server field-error map names (case-insensitive), as a set of the candidates. */
export function invalidFields(
  fields: Record<string, string[]> | null,
  candidates: readonly string[],
): ReadonlySet<string> {
  const hit = new Set<string>();
  for (const key of Object.keys(fields ?? {})) {
    const match = candidates.find((c) => c.toLowerCase() === key.toLowerCase());
    if (match && (fields?.[key]?.length ?? 0) > 0) hit.add(match);
  }
  return hit;
}
